import {
  type FC,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Tooltip, { type TooltipProps } from '@material-ui/core/Tooltip';
import cx from 'classnames';
import ResizeObserverPolyfill from 'resize-observer-polyfill';

import type { Callback } from 'src/lib/types';

import { useStyles } from './useStyles';

interface Props extends Omit<TooltipProps, 'title' | 'children'> {
  className?: string;
  title: ReactNode;
  children: (refCallback: Callback<HTMLElement | null>) => ReactElement;
}

export const TruncatedTooltip: FC<Props> = ({ className, title, children, ...restProps }) => {
  const styles = useStyles();

  const [element, setElement] = useState<HTMLElement | null>(null);
  const [overflowed, setOverflowed] = useState<boolean>(false);

  const classes = useMemo(
    () => ({
      tooltip: cx(styles.tooltip, className),
    }),
    [styles, className]
  );

  const updateOverflowed = useCallback((el: HTMLElement | null) => {
    if (!el) {
      setOverflowed(false);
      return;
    }

    setOverflowed(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
  }, []);

  const refCallback = useCallback(
    (ref: HTMLElement | null) => {
      setElement(ref);
      updateOverflowed(ref);
    },
    [updateOverflowed]
  );

  // Listen to element resizing
  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const resizeObserver = new ResizeObserverPolyfill(() => {
      if (element) {
        updateOverflowed(element);
      }
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [element, updateOverflowed]);

  // Listen to child changes
  useEffect(() => {
    updateOverflowed(element);
  }, [element, children, updateOverflowed]);

  return (
    <Tooltip
      classes={classes}
      title={overflowed && title ? title : ''}
      {...restProps}
    >
      {children(refCallback)}
    </Tooltip>
  );
};
